import { createEntity, readEntity } from "./delicDataClient";
import { getCachedUrl } from "../utils/StorageManager";

export const VIDEO_POSTER_STATUS = Object.freeze({
  NONE: "NONE",
  PENDING: "PENDING",
  CONVERTING: "CONVERTING",
  READY: "READY",
  FAILED: "FAILED",
});

export const VIDEO_POSTER_POLL_INTERVAL_MS = 3000;
export const VIDEO_POSTER_MAX_POLL_DURATION_MS = 30000;

const KNOWN_STATUSES = new Set(Object.values(VIDEO_POSTER_STATUS));

export function normalizeVideoPosterJob(job) {
  const status = job && KNOWN_STATUSES.has(job.status)
    ? job.status
    : VIDEO_POSTER_STATUS.NONE;

  return {
    jobId: job && job.jobId ? job.jobId : null,
    status,
    jobType: job && job.jobType ? job.jobType : "VIDEO_POSTER",
    sourceKey: job && job.sourceKey ? job.sourceKey : null,
    outputKey: job && (job.outputKey || job.previewKey)
      ? job.outputKey || job.previewKey
      : null,
    previewKey: job && (job.previewKey || job.outputKey)
      ? job.previewKey || job.outputKey
      : null,
    contentType: job && job.contentType ? job.contentType : "image/jpeg",
    format: job && job.format ? job.format : "jpeg",
    error: job && job.error ? job.error : null,
    retryCount: job && Number.isFinite(job.retryCount) ? job.retryCount : 0,
  };
}

export async function startVideoPoster(input) {
  return normalizeVideoPosterJob(
    await createEntity("VideoPoster", { input })
  );
}

export async function getVideoPosterJob(input) {
  return normalizeVideoPosterJob(
    await readEntity("VideoPosterJob", { input })
  );
}

export function getVideoPosterStorageLocation(sourceKey) {
  if (typeof sourceKey !== "string") return null;
  const match = /^protected\/([^/]+)\/(.+)$/.exec(sourceKey);
  if (!match) return null;

  const outputKey = `${sourceKey}.idea-poster.jpeg`;
  return {
    identityId: match[1],
    outputKey,
    storageKey: `${match[2]}.idea-poster.jpeg`,
  };
}

export async function signReadyVideoPoster(sourceKey) {
  const location = getVideoPosterStorageLocation(sourceKey);
  if (!location) return "";
  return getCachedUrl({
    identityId: location.identityId,
    key: location.storageKey,
  });
}

function waitForNextPoll(delayMs, signal) {
  return new Promise((resolve) => {
    if (signal && signal.aborted) {
      resolve(false);
      return;
    }

    const timeout = setTimeout(() => {
      if (signal) signal.removeEventListener("abort", handleAbort);
      resolve(true);
    }, delayMs);
    function handleAbort() {
      clearTimeout(timeout);
      signal.removeEventListener("abort", handleAbort);
      resolve(false);
    }
    if (signal) signal.addEventListener("abort", handleAbort, { once: true });
  });
}

export function isVideoPosterTerminal(job) {
  return !!job && (
    job.status === VIDEO_POSTER_STATUS.READY ||
    job.status === VIDEO_POSTER_STATUS.FAILED ||
    job.status === VIDEO_POSTER_STATUS.NONE
  );
}

export async function pollVideoPosterJob(
  input,
  {
    signal,
    onStatus,
    intervalMs = VIDEO_POSTER_POLL_INTERVAL_MS,
    maxDurationMs = VIDEO_POSTER_MAX_POLL_DURATION_MS,
  } = {}
) {
  const maxAttempts = Math.max(1, Math.ceil(maxDurationMs / intervalMs));
  let latest = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (!(await waitForNextPoll(intervalMs, signal))) return latest;
    if (signal && signal.aborted) return latest;

    latest = await getVideoPosterJob(input);
    if (signal && signal.aborted) return latest;
    if (onStatus) onStatus(latest);
    if (isVideoPosterTerminal(latest)) return latest;
  }

  return latest;
}
